/**
 * The week's review (handoff 2026-10-08; issue #77, moved out of Vaktplan.tsx): every point a
 * manager should look over in the window on screen, grouped by job. Pure: the page hands it what
 * it already holds (the rows, the server's rule entries, the staffing need, the pending offers and
 * swaps); nothing is inferred from an icon. Tested in review.test.ts.
 */
import type { ApiOffer, ApiShift, ApiSwap } from '@/api/data';
import { T } from '@/i18n';
import { type Entry, osloISO, plannedHoursOn, ruleText } from '@/rules';
import type { NeedProfile } from '@/shell/local';
import { DN, fmtDs, fullName, num } from '@/ui/format';
import { dow } from '@timefly/core';
import { cardStatus } from './Card';
import type { ReviewItem } from './Panel';
import type { Grid, Row } from './model';
import { gapText, hourGaps, needHours, pieces, workOf } from './staffing';

/**
 * A review item with what the Saker case draws as its figure (prototype case-templates, owner
 * 2026-10-28): a staffing day's need and people per hour, a shift rule's own entry.
 */
export interface WeekItem extends ReviewItem {
  /** per hour the need names: the fewest people planned at once in it, and the need */
  staff?: { h: number; have: number; need: number }[];
  /** the planned hours of the day and the need's total */
  hours?: { planned: number; need: number };
  /** the rule entry on a shift, with its figures (minutes, hours, max) */
  entry?: Entry;
}

/** People planned per hour of the need: the fewest at once in the hour, as hourGaps counts them. */
export function staffCols(need: NeedProfile, date: string, shifts: ApiShift[]) {
  const day = need[dow(date)] ?? {};
  const work = workOf(shifts);
  return Object.keys(day)
    .map(Number)
    .filter((h) => (Number(day[h]) || 0) > 0)
    .sort((a, b) => a - b)
    .map((h) => {
      const a = Date.parse(osloISO(date, `${String(h).padStart(2, '0')}:00`));
      const have = Math.min(...pieces(work, a, a + 36e5).map((p) => p.have));
      return { h, have, need: Number(day[h]) };
    });
}

export interface ReviewInput {
  /** the rows on screen, filters included */
  rows: Row[];
  days: string[];
  grid: Pick<Grid, 'entries'>;
  shifts: ApiShift[];
  offers: ApiOffer[];
  swaps: ApiSwap[];
  /** the shift ids the grid shows: a request counts only for a shift on screen */
  visible: Set<number>;
  need: NeedProfile;
  /** staffing items only when the need is shown (Bemanning mot behov) */
  staffView: boolean;
  /** the catalogue's translate, for the rule sentences */
  t: (k: string, v?: Record<string, string | number>) => string;
}

export function reviewOf({
  rows,
  days,
  grid,
  shifts,
  offers,
  swaps,
  visible,
  need,
  staffView,
  t,
}: ReviewInput): WeekItem[] {
  const out: WeekItem[] = [];
  const at = (d: string) => `${DN[dow(d)]} ${fmtDs(d)}`;
  // staffing: a day planned under its need, and the hours short of it (hour-by-hour cover)
  if (staffView)
    for (const d of days) {
      const nh = needHours(need, d);
      if (nh == null) continue;
      const gaps = hourGaps(need, d, shifts);
      // the part of every assigned shift on this date, a night from yesterday included (retest R05)
      const planned = shifts.filter((x) => x.user_id != null).reduce((a, x) => a + plannedHoursOn(x, d), 0);
      const under = planned + 0.05 < nh;
      // one item a day: the total when it is short, and the hours, unless nobody is planned at all
      const hours =
        planned > 0 && gaps.length
          ? T('under behovet kl. {list}', {
              list: gaps
                .map(
                  (g) =>
                    `${gapText(g)} (${T('{n} av {m}', { n: num(Math.round(g.have * 10) / 10), m: num(g.need) })})`,
                )
                .join(', '),
            })
          : '';
      const total = under
        ? T('{h} t planlagt mot {n} t behov', { h: num(Math.round(planned * 10) / 10), n: num(nh) })
        : '';
      if (total || hours)
        out.push({
          key: `n${d}`,
          group: 'staff',
          sev: 'w',
          who: at(d),
          text:
            total && hours ? `${total}; ${hours}` : total || hours.charAt(0).toUpperCase() + hours.slice(1),
          day: d,
          staff: staffCols(need, d, shifts),
          hours: { planned, need: nh },
        });
    }
  for (const r of rows) {
    const name = fullName(r.user);
    for (const e of r.week.entries)
      out.push({
        key: `w${r.user.id}${e.code}`,
        group: 'rules',
        sev: e.sev,
        who: name,
        text: ruleText(t, e),
        person: r.user.id,
        rule: e,
      });
    for (const d of days) {
      const c = r.cells.get(d);
      for (const x of c?.shifts ?? []) {
        for (const [i, e] of (grid.entries.get(x.id) ?? []).entries())
          out.push({
            key: `s${x.id}-${i}`,
            group: e.code === 'open' ? 'attend' : 'rules',
            sev: e.sev,
            who: `${name}, ${at(d)}`,
            text: ruleText(t, e),
            shiftId: x.id,
            code: e.code,
            entry: e,
          });
        for (const m of cardStatus(x))
          if (m.key === 'punch' && m.tone === 'bad')
            out.push({
              key: `p${x.id}`,
              group: 'attend',
              sev: 'w',
              who: `${name}, ${at(d)}`,
              text: m.text,
              shiftId: x.id,
            });
        if (c?.absence && c.absence.status !== 'declined')
          out.push({
            key: `a${x.id}`,
            group: 'absence',
            sev: c.absence.status === 'pending' ? 'w' : 'i',
            who: `${name}, ${at(d)}`,
            text:
              c.absence.status === 'pending'
                ? T('{type} venter på svar, vakten står', {
                    type: c.absence.absence_type?.name ?? T('Fravær'),
                  })
                : T('{type} godkjent, vakten står fortsatt', {
                    type: c.absence.absence_type?.name ?? T('Fravær'),
                  }),
            shiftId: x.id,
            absence: c.absence,
          });
        if (c?.avail?.kind === 'unavail')
          out.push({
            key: `v${x.id}`,
            group: 'avail',
            sev: 'w',
            who: `${name}, ${at(d)}`,
            text: T('Har sagt at de ikke kan jobbe'),
            shiftId: x.id,
          });
      }
    }
  }
  const onScreen = (id: number | undefined) => id != null && visible.has(id);
  for (const o of offers)
    if (onScreen(o.shift_id))
      out.push({
        key: `o${o.id}`,
        group: 'requests',
        sev: 'i',
        who: fullName(o.offeredByUser),
        text: T('vil gi bort en vakt'),
        shiftId: o.shift_id,
        request: { what: 'offer', id: o.id },
      });
  for (const x of swaps)
    if (onScreen(x.shift_id_one) || onScreen(x.shift_id_two))
      out.push({
        key: `x${x.id}`,
        group: 'requests',
        sev: 'i',
        who: `${fullName(x.offeredByUserOne)} ${T('og')} ${fullName(x.offeredByUserTwo)}`,
        text: T('vil bytte vakter'),
        shiftId: onScreen(x.shift_id_one) ? x.shift_id_one : x.shift_id_two,
        request: { what: 'swap', id: x.id },
      });
  return out;
}
